/**
 * ConnectionStatusIndicator - Real-time connection status display
 * Shows connected/disconnected state with animation
 */

import { memo } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { ConnectionState } from '../hooks/useRealtimeSync';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';

const ConnectionStatusIndicator = memo(({ 
    connectionState, 
    isOnline = true,
    reconnectAttempts = 0,
    size = 'default', // 'small', 'default', 'large'
    showLabel = true,
    className = ''
}) => {
    const config = {
        [ConnectionState.CONNECTED]: {
            icon: Wifi,
            color: 'text-green-500',
            bgColor: 'bg-green-500',
            label: 'Connecté',
            tooltip: 'Synchronisation temps réel active',
            pulse: false
        },
        [ConnectionState.CONNECTING]: {
            icon: RefreshCw,
            color: 'text-yellow-500',
            bgColor: 'bg-yellow-500',
            label: 'Connexion...',
            tooltip: 'Connexion en cours...',
            pulse: true,
            spin: true
        },
        [ConnectionState.RECONNECTING]: {
            icon: RefreshCw,
            color: 'text-orange-500',
            bgColor: 'bg-orange-500',
            label: `Reconnexion (${reconnectAttempts})`,
            tooltip: `Tentative de reconnexion #${reconnectAttempts}`,
            pulse: true,
            spin: true
        },
        [ConnectionState.DISCONNECTED]: {
            icon: WifiOff,
            color: 'text-gray-500',
            bgColor: 'bg-gray-500',
            label: 'Hors ligne',
            tooltip: 'Non connecté au serveur',
            pulse: false
        },
        [ConnectionState.ERROR]: {
            icon: AlertTriangle,
            color: 'text-red-500',
            bgColor: 'bg-red-500',
            label: 'Erreur',
            tooltip: 'Erreur de connexion',
            pulse: true
        }
    };
    
    const sizeConfig = {
        small: {
            iconSize: 'w-3 h-3',
            dotSize: 'w-1.5 h-1.5',
            textSize: 'text-[10px]',
            gap: 'gap-1',
            padding: 'px-1.5 py-0.5'
        },
        default: {
            iconSize: 'w-4 h-4',
            dotSize: 'w-2 h-2',
            textSize: 'text-xs',
            gap: 'gap-1.5',
            padding: 'px-2 py-1'
        },
        large: {
            iconSize: 'w-5 h-5',
            dotSize: 'w-2.5 h-2.5',
            textSize: 'text-sm',
            gap: 'gap-2',
            padding: 'px-3 py-1.5'
        }
    };
    
    const state = !isOnline ? ConnectionState.DISCONNECTED : connectionState;
    const stateConfig = config[state] || config[ConnectionState.DISCONNECTED];
    const sizes = sizeConfig[size] || sizeConfig.default;
    const Icon = stateConfig.icon;
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className={cn(
                        "flex items-center rounded-full border",
                        sizes.gap,
                        sizes.padding,
                        state === ConnectionState.CONNECTED 
                            ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800"
                            : state === ConnectionState.ERROR
                                ? "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"
                                : "bg-muted/50 border-muted-foreground/20",
                        className
                    )}>
                        {/* Status dot */}
                        <div className={cn(
                            "rounded-full",
                            sizes.dotSize,
                            stateConfig.bgColor,
                            stateConfig.pulse && "animate-pulse"
                        )} />
                        
                        {/* Icon */}
                        <Icon className={cn(
                            sizes.iconSize,
                            stateConfig.color,
                            stateConfig.spin && "animate-spin"
                        )} />
                        
                        {/* Label */}
                        {showLabel && (
                            <span className={cn(
                                sizes.textSize,
                                "font-medium",
                                stateConfig.color
                            )}>
                                {stateConfig.label}
                            </span>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{stateConfig.tooltip}</p>
                    {!isOnline && <p className="text-xs text-muted-foreground">Vérifiez votre connexion internet</p>}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
});

ConnectionStatusIndicator.displayName = 'ConnectionStatusIndicator';

export default ConnectionStatusIndicator;
